import React, { useState } from 'react';
import { Box, Button, FormControl, FormLabel, Input, Text, Flex, useToast, Heading, SimpleGrid, VStack, HStack, Divider, Icon, Badge, Table, Thead, Tbody, Tr, Th, Td, TableContainer } from '@chakra-ui/react';
import { FaFileInvoice, FaMagic, FaCheckCircle, FaFilePdf, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';
import { appRequest } from "routes";

const AgreementInvoiceExtractor = () => {
    const [agreementFile, setAgreementFile] = useState<File | null>(null);
    const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<any>(null);
    const toast = useToast();

    const toBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    };

    const handleSubmit = async () => {
        if (!agreementFile || !invoiceFile) {
            toast({
                title: "Validation Error",
                description: "Please upload both Agreement and Invoice PDF files.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        try {
            setLoading(true);
            const agreementBase64 = await toBase64(agreementFile);
            const invoiceBase64 = await toBase64(invoiceFile);

            const payload = {
                agreement: agreementBase64,
                invoice: invoiceBase64
            };

            const response: any = await appRequest("document", "extractAgreementInvoice", payload);

            if (response && response.status === "success") {
                setResult(response.data);
                toast({
                    title: "Extraction Successful",
                    description: "Data successfully extracted from documents.",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                throw new Error(response?.message || "Extraction failed");
            }
        } catch (error: any) {
            console.error("Extraction error", error);
            toast({
                title: "Extraction Error",
                description: error.message || "Something went wrong during extraction.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box p={{ base: 4, md: 8 }} bg="white" borderRadius="xl" shadow="md" borderWidth="1px" w="full" minH="full">
            <Flex alignItems="center" mb={8} borderBottomWidth="1px" pb={6} borderColor="gray.100">
                <Box p={3} bg="teal.50" color="teal.500" borderRadius="xl" mr={5}>
                    <Icon as={FaFileInvoice} boxSize={7} />
                </Box>
                <Box>
                    <Heading size="lg" color="gray.800" fontWeight="bold">Data Extractor</Heading>
                    <Text color="gray.500" fontSize="sm" mt={1}>Upload your Agreement and Invoice PDFs to automatically extract key financial data.</Text>
                </Box>
            </Flex>

            <VStack spacing={6} align="stretch" bg="gray.50" p={6} borderRadius="xl" borderWidth="1px" borderStyle="dashed" borderColor="gray.300">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                    <FormControl isRequired>
                        <FormLabel fontWeight="semibold" color="gray.700" mb={3}>Agreement Document</FormLabel>
                        <Flex alignItems="center" bg="white" borderWidth="1px" borderRadius="md" p={2} _hover={{ borderColor: "teal.400", shadow: "sm" }} transition="all 0.2s">
                            <Icon as={FaFilePdf} color="red.400" boxSize={6} mx={3} />
                            <Input
                                type="file"
                                accept="application/pdf"
                                onChange={(e) => setAgreementFile(e.target.files?.[0] || null)}
                                variant="unstyled"
                                py={2}
                                sx={{
                                    '::file-selector-button': {
                                        border: 'none',
                                        background: 'gray.100',
                                        padding: '0.5rem 1rem',
                                        borderRadius: '0.375rem',
                                        cursor: 'pointer',
                                        marginRight: '1rem',
                                        fontWeight: 'semibold',
                                        color: 'gray.700',
                                        transition: 'all 0.2s',
                                    },
                                    '::file-selector-button:hover': {
                                        background: 'gray.200',
                                    }
                                }}
                            />
                        </Flex>
                    </FormControl>

                    <FormControl isRequired>
                        <FormLabel fontWeight="semibold" color="gray.700" mb={3}>Invoice Document</FormLabel>
                        <Flex alignItems="center" bg="white" borderWidth="1px" borderRadius="md" p={2} _hover={{ borderColor: "teal.400", shadow: "sm" }} transition="all 0.2s">
                            <Icon as={FaFilePdf} color="red.400" boxSize={6} mx={3} />
                            <Input
                                type="file"
                                accept="application/pdf"
                                onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
                                variant="unstyled"
                                py={2}
                                sx={{
                                    '::file-selector-button': {
                                        border: 'none',
                                        background: 'gray.100',
                                        padding: '0.5rem 1rem',
                                        borderRadius: '0.375rem',
                                        cursor: 'pointer',
                                        marginRight: '1rem',
                                        fontWeight: 'semibold',
                                        color: 'gray.700',
                                        transition: 'all 0.2s',
                                    },
                                    '::file-selector-button:hover': {
                                        background: 'gray.200',
                                    }
                                }}
                            />
                        </Flex>
                    </FormControl>
                </SimpleGrid>

                <Flex justify="flex-end" pt={4}>
                    <Button
                        colorScheme="teal"
                        size="lg"
                        onClick={handleSubmit}
                        isLoading={loading}
                        loadingText="Extracting Data..."
                        leftIcon={<FaMagic />}
                        w={{ base: "full", md: "250px" }}
                        boxShadow="md"
                        _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                        transition="all 0.2s"
                        borderRadius="full"
                    >
                        Extract Data
                    </Button>
                </Flex>
            </VStack>

            {result && (
                <Box mt={10} p={6} bg="white" borderRadius="xl" borderWidth="1px" shadow="sm" borderColor="teal.100" position="relative" overflow="hidden">
                    <Box position="absolute" top={0} left={0} w="4px" h="full" bg="teal.400" />

                    <Flex alignItems="center" mb={6}>
                        <Icon as={FaCheckCircle} color="teal.500" boxSize={6} mr={3} />
                        <Heading size="md" color="gray.800">Extraction Results</Heading>
                        <Badge ml={4} colorScheme="teal" variant="subtle" px={3} py={1} borderRadius="full" fontSize="xs" fontWeight="bold">SUCCESS</Badge>
                    </Flex>

                    {result.success ? (
                        <VStack align="stretch" spacing={8}>
                            {result.results && (
                                <Box>
                                    <Heading size="xs" mb={4} color="gray.500" textTransform="uppercase" letterSpacing="wider">Extracted Dates Comparison</Heading>
                                    <TableContainer borderRadius="lg" borderWidth="1px" borderColor="gray.200" bg="white" shadow="sm">
                                        <Table variant="simple" size="md">
                                            <Thead bg="gray.50">
                                                <Tr>
                                                    <Th color="gray.600" w="25%">Attribute</Th>
                                                    <Th color="gray.600" w="25%">Agreement Document</Th>
                                                    <Th color="gray.600" w="25%">Invoice Document</Th>
                                                    <Th color="gray.600" w="25%" textAlign="center">Logical Comparison</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                <Tr _hover={{ bg: "gray.50" }}>
                                                    <Td fontWeight="bold" color="gray.700">Start / Issuance Date</Td>
                                                    <Td>{result.results.agreement_start_date || "N/A"}</Td>
                                                    <Td>{result.results.invoice_date || "N/A"}</Td>
                                                    <Td rowSpan={2} borderLeftWidth="1px" borderColor="gray.200" verticalAlign="middle">
                                                        {(() => {
                                                            const parseDt = (d: string) => {
                                                                if (!d || d === "N/A" || d === "null") return null;
                                                                const p = d.split('-');
                                                                if (p.length === 3) return new Date(`${p[2]}-${p[1]}-${p[0]}`).getTime();
                                                                return null;
                                                            };
                                                            const sDate = parseDt(result.results.agreement_start_date);
                                                            const eDate = parseDt(result.results.agreement_original_end_date);
                                                            const iDate = parseDt(result.results.invoice_date);
                                                            
                                                            if (iDate && sDate && eDate) {
                                                                if (iDate >= sDate && iDate <= eDate) {
                                                                    return (
                                                                        <Flex direction="column" align="center" justify="center">
                                                                            <Icon as={FaCheckCircle} color="teal.500" boxSize={6} mb={2} />
                                                                            <Text color="teal.600" fontWeight="bold" fontSize="sm" textAlign="center">VALID</Text>
                                                                            <Text color="gray.500" fontSize="xs" textAlign="center" mt={1}>Invoice date falls within the agreement period</Text>
                                                                        </Flex>
                                                                    );
                                                                } else {
                                                                    return (
                                                                        <Flex direction="column" align="center" justify="center">
                                                                            <Icon as={FaExclamationTriangle} color="red.500" boxSize={6} mb={2} />
                                                                            <Text color="red.600" fontWeight="bold" fontSize="sm" textAlign="center">INVALID</Text>
                                                                            <Text color="gray.500" fontSize="xs" textAlign="center" mt={1}>Invoice date is outside the agreement period</Text>
                                                                        </Flex>
                                                                    );
                                                                }
                                                            }
                                                            return <Text color="gray.500" fontSize="xs" textAlign="center">Insufficient data for comparison</Text>;
                                                        })()}
                                                    </Td>
                                                </Tr>
                                                <Tr _hover={{ bg: "gray.50" }}>
                                                    <Td fontWeight="bold" color="gray.700">End / Due Date</Td>
                                                    <Td>{result.results.agreement_original_end_date || "N/A"}</Td>
                                                    <Td>{result.results.invoice_due_date || "N/A"}</Td>
                                                </Tr>
                                            </Tbody>
                                        </Table>
                                    </TableContainer>

                                    {result.results && (
                                        <Box mt={8}>
                                            <Flex alignItems="center" mb={4}>
                                                {result.results.alert_level && (
                                                    <Badge
                                                        colorScheme={result.results.alert_level === 'GREEN' ? 'green' : result.results.alert_level === 'RED' ? 'red' : 'orange'}
                                                        fontSize="sm" px={3} py={1} borderRadius="md" mr={3}
                                                    >
                                                        {result.results.alert_level}
                                                    </Badge>
                                                )}
                                                <Heading size="sm" color="gray.700" textTransform="uppercase" letterSpacing="wider">All Extracted Data</Heading>
                                            </Flex>

                                            <TableContainer borderRadius="lg" borderWidth="1px" borderColor="gray.200" bg="white" shadow="sm">
                                                <Table variant="simple" size="sm">
                                                    <Thead bg="gray.50">
                                                        <Tr>
                                                            <Th color="gray.600" w="30%">Field</Th>
                                                            <Th color="gray.600" w="70%">Value</Th>
                                                        </Tr>
                                                    </Thead>
                                                    <Tbody>
                                                        {Object.entries(result.results).map(([key, value]: any) => {
                                                            if (key === 'alert_summary') return null; // Hide alert_summary as requested
                                                            
                                                            // Format key from snake_case to Title Case
                                                            const formattedKey = key
                                                                .replace(/_/g, ' ')
                                                                .replace(/\b\w/g, (l: string) => l.toUpperCase());

                                                            return (
                                                                <Tr key={key} _hover={{ bg: "gray.50" }}>
                                                                    <Td fontWeight="bold" color="gray.700" whiteSpace="normal">
                                                                        {formattedKey}
                                                                    </Td>
                                                                    <Td whiteSpace="normal" color="gray.600">
                                                                        {value === null || value === undefined ? "N/A" : String(value)}
                                                                    </Td>
                                                                </Tr>
                                                            );
                                                        })}
                                                    </Tbody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </VStack>
                    ) : (
                        <Box as="pre" overflowX="auto" fontSize="sm" p={4} bg="gray.50" borderRadius="md">
                            {JSON.stringify(result, null, 2)}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default AgreementInvoiceExtractor;
